import {
  domains as fallbackDomains,
  getSkillDomainColor,
  type Domain,
  type DomainId,
  type Skill,
} from "@/data/prooftree"

export type ImmersionDomainState = {
  domainId: DomainId
  immersionLevel: number
  unlockedSkillIds: string[]
  lockedPreviewSkillIds: string[]
  unlockReason: string
}

export type NextFocusItem = {
  id: string
  kind: "skill" | "locked-preview"
  skillId: string
  title: string
  reason: string
  actionLabel: string
  route: string
  accentColor: string
  locked: boolean
  priority: number
}

export type ProofTreeImmersionState = {
  domains: ImmersionDomainState[]
  unlockedSkillIds: string[]
  lockedPreviewSkillIds: string[]
  nextFocusItems: NextFocusItem[]
}

const MAX_NEXT_FOCUS_ITEMS = 7
const MAX_LOCKED_PREVIEWS_PER_DOMAIN = 2

function hasSkillActivity(skill: Skill) {
  return skill.completedLevels > 0 || skill.levels.some((level) => level.status === "In Progress")
}

function getStageSkills(domain: Domain, stage: number) {
  return domain.skills.filter((skill) => skill.unlockStage === stage)
}

function getVisibleStageSkills(domain: Domain, stage: number) {
  return domain.skills.filter((skill) => skill.unlockStage <= stage)
}

function hasMeaningfulCoverage(skills: Skill[]) {
  if (skills.length === 0) return false
  const activeSkillCount = skills.filter(hasSkillActivity).length
  return activeSkillCount / skills.length >= 0.5
}

function getDomainImmersionLevel(domain: Domain) {
  let immersionLevel = 1

  for (let stage = 1; stage <= 3; stage += 1) {
    const stageSkills = getStageSkills(domain, stage)
    const visibleSkills = getVisibleStageSkills(domain, stage)

    if (hasMeaningfulCoverage(stageSkills) && hasMeaningfulCoverage(visibleSkills)) {
      immersionLevel = Math.min(4, stage + 1)
    }
  }

  return immersionLevel
}

function getUnlockedSkills(domain: Domain, immersionLevel: number) {
  return domain.skills.filter((skill) => skill.unlockStage <= immersionLevel || immersionLevel >= 4)
}

function getLockedPreviewSkills(domain: Domain, immersionLevel: number) {
  if (immersionLevel >= 4) return []

  return domain.skills
    .filter((skill) => skill.unlockStage === immersionLevel + 1)
    .slice(0, MAX_LOCKED_PREVIEWS_PER_DOMAIN)
}

function getUnlockReason(domain: Domain, immersionLevel: number, previewSkills: Skill[]) {
  if (immersionLevel >= 4) {
    return `${domain.shortTitle} mastery is open. Use the full domain as a capstone layer.`
  }

  const nextSkillNames = previewSkills.map((skill) => skill.title).join(", ")
  return nextSkillNames
    ? `Practice the visible ${domain.shortTitle} skills to unlock ${nextSkillNames}.`
    : `Practice the visible ${domain.shortTitle} skills to unlock the next immersion level.`
}

function getFocusPriority(skill: Skill, locked: boolean) {
  if (locked) return 10 - skill.unlockStage
  const activeLevelBonus = skill.levels.some((level) => level.status === "In Progress") ? 100 : 0
  const lowProgressBonus = Math.max(0, 100 - skill.progress)
  return activeLevelBonus + lowProgressBonus + (4 - skill.unlockStage) * 10
}

function buildFocusItem(skill: Skill, domain: Domain, locked: boolean): NextFocusItem {
  const nextOpenLevel = skill.levels.find((level) => level.status === "In Progress")
  const reason = locked
    ? `Coming next in ${domain.title}. ${skill.whyItMatters}`
    : nextOpenLevel
      ? `${domain.title} · ${nextOpenLevel.title}. ${nextOpenLevel.action}`
      : `${domain.title} · Add one proof to make this skill visible.`

  return {
    id: `${locked ? "locked" : "focus"}-${skill.id}`,
    kind: locked ? "locked-preview" : "skill",
    skillId: skill.id,
    title: skill.title,
    reason,
    actionLabel: locked ? "Unlock next" : "Coach this",
    route: `/skill/${skill.id}${locked ? "?locked=1" : ""}`,
    accentColor: getSkillDomainColor(skill.id),
    locked,
    priority: getFocusPriority(skill, locked),
  }
}

export function buildProofTreeImmersion(sourceDomains: Domain[] = fallbackDomains) {
  const domainStates = sourceDomains.map((domain): ImmersionDomainState => {
    const immersionLevel = getDomainImmersionLevel(domain)
    const unlockedSkills = getUnlockedSkills(domain, immersionLevel)
    const lockedPreviewSkills = getLockedPreviewSkills(domain, immersionLevel)

    return {
      domainId: domain.id,
      immersionLevel,
      unlockedSkillIds: unlockedSkills.map((skill) => skill.id),
      lockedPreviewSkillIds: lockedPreviewSkills.map((skill) => skill.id),
      unlockReason: getUnlockReason(domain, immersionLevel, lockedPreviewSkills),
    }
  })

  const domainStateById = new Map(domainStates.map((state) => [state.domainId, state]))
  const focusItems = sourceDomains.flatMap((domain) => {
    const domainState = domainStateById.get(domain.id)
    if (!domainState) return []

    const unlockedSkillIds = new Set(domainState.unlockedSkillIds)
    const lockedPreviewSkillIds = new Set(domainState.lockedPreviewSkillIds)
    const unlockedItems = domain.skills
      .filter((skill) => unlockedSkillIds.has(skill.id))
      .map((skill) => buildFocusItem(skill, domain, false))
    const lockedItems = domain.skills
      .filter((skill) => lockedPreviewSkillIds.has(skill.id))
      .map((skill) => buildFocusItem(skill, domain, true))

    return [...unlockedItems, ...lockedItems]
  })

  return {
    domains: domainStates,
    unlockedSkillIds: Array.from(new Set(domainStates.flatMap((state) => state.unlockedSkillIds))),
    lockedPreviewSkillIds: Array.from(
      new Set(domainStates.flatMap((state) => state.lockedPreviewSkillIds)),
    ),
    nextFocusItems: focusItems
      .sort((a, b) => b.priority - a.priority)
      .slice(0, MAX_NEXT_FOCUS_ITEMS),
  } satisfies ProofTreeImmersionState
}

export function getDomainImmersionState(
  domainId: DomainId,
  immersionState: ProofTreeImmersionState,
) {
  return immersionState.domains.find((domainState) => domainState.domainId === domainId)
}

export function isSkillUnlocked(skillId: string, immersionState: ProofTreeImmersionState) {
  return immersionState.unlockedSkillIds.includes(skillId)
}

export function isSkillLockedPreview(skillId: string, immersionState: ProofTreeImmersionState) {
  return immersionState.lockedPreviewSkillIds.includes(skillId)
}
